import { EVENT_COLORS } from './calendarAggregate';

export type DossierCalendarEventKind = 'hearing' | 'prescription' | 'task' | 'appointment';

export const DOSSIER_CALENDAR_COLORS: Record<DossierCalendarEventKind, string> = {
  hearing: EVENT_COLORS.hearing,
  task: EVENT_COLORS.task,
  prescription: '#dc2626',
  appointment: EVENT_COLORS.appointment,
};

export const DOSSIER_CALENDAR_LEGEND: ReadonlyArray<{
  kind: DossierCalendarEventKind;
  label: string;
  color: string;
}> = [
  { kind: 'hearing', label: 'Audiences', color: DOSSIER_CALENDAR_COLORS.hearing },
  { kind: 'task', label: 'Tâches', color: DOSSIER_CALENDAR_COLORS.task },
  { kind: 'prescription', label: 'Prescription', color: DOSSIER_CALENDAR_COLORS.prescription },
  { kind: 'appointment', label: 'Rendez-vous', color: DOSSIER_CALENDAR_COLORS.appointment },
];

export interface DossierCalendarDossier {
  id: string;
  next_hearing_at: string | null;
  prescription_date: string | null;
}

export interface DossierCalendarHearing {
  id: string;
  starts_at: string;
  label: string;
}

export interface DossierCalendarTask {
  id: string;
  content: string;
  deadline: string | null;
  due_date: string | null;
  due_datetime: string | null;
  is_completed?: boolean;
  is_deleted?: boolean;
}

export interface DossierCalendarAppointment {
  id: string;
  title: string;
  starts_at: string;
  ends_at: string;
}

export interface DossierCalendarEvent {
  id: string;
  kind: DossierCalendarEventKind;
  title: string;
  start: string;
  end?: string;
  allDay: boolean;
  href: string | null;
  taskId: string | null;
  hearingId?: string | null;
  appointmentId?: string | null;
  color: string;
}

export type DossierCalendarClick =
  | { action: 'openTask'; taskId: string }
  | { action: 'openHearing'; hearingId: string }
  | { action: 'openAppointment'; appointmentId: string }
  | { action: 'navigate'; href: string };

export function aggregateDossierCalendarEvents(
  dossier: DossierCalendarDossier,
  tasks: DossierCalendarTask[],
  hearings?: DossierCalendarHearing[],
  appointments?: DossierCalendarAppointment[],
): DossierCalendarEvent[] {
  const out: DossierCalendarEvent[] = [];
  const resumeHref = `/dossiers/${dossier.id}/resume`;

  if (hearings) {
    for (const h of hearings) {
      out.push({
        id: `hearing:${h.id}`,
        kind: 'hearing',
        title: h.label,
        start: h.starts_at,
        allDay: false,
        href: resumeHref,
        taskId: null,
        hearingId: h.id,
        color: DOSSIER_CALENDAR_COLORS.hearing,
      });
    }
  } else if (dossier.next_hearing_at) {
    out.push({
      id: `hearing:${dossier.id}`,
      kind: 'hearing',
      title: 'Audience',
      start: dossier.next_hearing_at,
      allDay: false,
      href: resumeHref,
      taskId: null,
      color: DOSSIER_CALENDAR_COLORS.hearing,
    });
  }

  if (dossier.prescription_date) {
    out.push({
      id: `prescription:${dossier.id}`,
      kind: 'prescription',
      title: 'Prescription',
      start: dossier.prescription_date,
      allDay: true,
      href: resumeHref,
      taskId: null,
      color: DOSSIER_CALENDAR_COLORS.prescription,
    });
  }

  for (const a of appointments ?? []) {
    out.push({
      id: `appointment:${a.id}`,
      kind: 'appointment',
      title: a.title,
      start: a.starts_at,
      end: a.ends_at,
      allDay: false,
      href: `/dossiers/${dossier.id}/agenda?appointment=${encodeURIComponent(a.id)}`,
      taskId: null,
      appointmentId: a.id,
      color: DOSSIER_CALENDAR_COLORS.appointment,
    });
  }

  for (const t of tasks) {
    if (t.is_completed || t.is_deleted) continue;
    let start: string | null = null;
    let allDay = true;
    if (t.due_datetime) {
      start = t.due_datetime;
      allDay = false;
    } else if (t.due_date) {
      start = t.due_date;
    } else if (t.deadline) {
      start = t.deadline;
    }
    if (!start) continue;
    out.push({
      id: `task:${t.id}`,
      kind: 'task',
      title: t.content,
      start,
      allDay,
      href: null,
      taskId: t.id,
      color: DOSSIER_CALENDAR_COLORS.task,
    });
  }

  out.sort((a, b) => a.start.localeCompare(b.start));
  return out;
}

export function resolveDossierCalendarClick(
  event: DossierCalendarEvent,
): DossierCalendarClick | null {
  if (event.kind === 'task' && event.taskId) {
    return { action: 'openTask', taskId: event.taskId };
  }
  if (event.kind === 'hearing' && event.hearingId) {
    return { action: 'openHearing', hearingId: event.hearingId };
  }
  if (event.kind === 'appointment' && event.appointmentId) {
    return { action: 'openAppointment', appointmentId: event.appointmentId };
  }
  if (event.href) {
    return { action: 'navigate', href: event.href };
  }
  return null;
}
